// frontend/src/pages/admin/PlayerAchievements.jsx
import React, { useCallback, useEffect, useState, useMemo } from "react";
import API from "../../api/api";
import Navbar from "../../components/Navbar";
import SearchableSelect from "../../components/common/SearchableSelect";
import Swal from "sweetalert2";
import { FaCheckCircle, FaFileAlt } from "react-icons/fa";
import { resolveAssetUrl } from "../../api/config";

const PlayerAchievements = () => {
  const [activeSubTab, setActiveSubTab] = useState("manage");
  const [pendingAchievements, setPendingAchievements] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("All");
  const [currentUser, setCurrentUser] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);

  const [sports, setSports] = useState([]);
  const [games, setGames] = useState([]);
  const [playerClassifications, setPlayerClassifications] = useState([]);

  const [form, setForm] = useState({
    id: null,
    game_id: "",
    sport_ui: "", // sport id for UI
    event_name: "",
    event_location: "",
    event_level: "District",
    medal: "None",
    event_date: "",
    notes: "",
  });

  // --------- Players ----------
  const loadPlayers = async () => {
    try {
      setLoading(true);
      // Call new full-list endpoint
      let res;
      try {
        res = await API.fetchAllPlayersFull();
      } catch (e) {
        // Fallback to paginated endpoint when /all-players is unavailable
        res = await API.fetchAdminPlayers();
      }
      // res should already be an array thanks to interceptor, but be defensive:
      const playersData = Array.isArray(res)
        ? res
        : res?.data?.data || res?.data || res?.players || [];

      setPlayers(playersData);

      if (playersData.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No Players",
          text: "Add players first in Players Management",
        });
      }
    } catch (err) {
      console.error("Players load error:", err);
      Swal.fire("Failed to load players");
    } finally {
      setLoading(false);
    }
  };

  // --------- Sports & Games ----------
  const loadSports = async () => {
    try {
      const res = await API.getSports();
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setSports(list);
    } catch (err) {
      console.error("Sports load error:", err);
      setSports([]);
    }
  };

  const loadGames = async (sportId) => {
    try {
      if (!sportId) {
        setGames([]);
        return;
      }
      const res = await API.fetchGamesBySport(sportId);
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setGames(list);
    } catch (err) {
      console.error("Games load error:", err);
      setGames([]);
    }
  };

  // --------- Achievements ----------
  const loadAchievements = useCallback(async () => {
    if (!selectedPlayer) {
      setAchievements([]);
      return;
    }

    try {
      const res = await API.fetchPlayerAchievements(selectedPlayer);
      const achievementsData = Array.isArray(res) ? res : [];
      setAchievements(achievementsData);
    } catch (err) {
      console.error(
        "Achievements error:",
        err?.status,
        err?.message || err
      );
      Swal.fire({
        icon: "error",
        title: "Failed to load achievements",
        text: err?.message || "Unknown error",
      });
      setAchievements([]);
    }
  }, [selectedPlayer]);

  const loadPlayerClassifications = async (playerId) => {
    if (!playerId) {
      setPlayerClassifications([]);
      return;
    }
    try {
      const res = await API.fetchClassifiedPlayers({ player_id: playerId });
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setPlayerClassifications(list);
    } catch (err) {
      console.error("Classification load error:", err);
      setPlayerClassifications([]);
    }
  };

  const saveAchievement = async () => {
    if (!form.event_name.trim()) {
      Swal.fire("Event name required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await API.updatePlayerAchievement(editingId, {
          ...form,
          game_id: form.game_id || null,
        });
        Swal.fire("✅ Updated successfully!", "", "success");
      } else {
        await API.addPlayerAchievement({
          ...form,
          player_id: selectedPlayer,
          game_id: form.game_id || null,
        });
        Swal.fire("✅ Added successfully!", "", "success");
      }
      setForm({
        id: null,
        game_id: "",
        sport_ui: "",
        event_name: "",
        event_location: "",
        event_level: "District",
        medal: "None",
        event_date: "",
        notes: "",
      });
      setEditingId(null);
      loadAchievements();
    } catch (err) {
      console.error("Save error:", err);
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: err?.message || "Try again",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteAchievement = async (id) => {
    const result = await Swal.fire({
      title: "Delete achievement?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
    });

    if (result.isConfirmed) {
      try {
        await API.deletePlayerAchievement(id);
        Swal.fire("✅ Deleted!", "", "success");
        loadAchievements();
      } catch (err) {
        console.error("Delete error:", err);
        Swal.fire("❌ Delete failed", "", "error");
      }
    }
  };

  const editAchievement = (achievement) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setEditingId(achievement.id);
    setForm({
      id: achievement.id,
      game_id: achievement.game_id || "",
      sport_ui: "", // will be set if user chooses sport filter
      event_name: achievement.event_name || "",
      event_location: achievement.event_location || "",
      event_level: achievement.event_level || "District",
      medal: achievement.medal || "None",
      event_date: achievement.event_date || "",
      notes: achievement.notes || "",
    });

    // best effort: find sport by name and load its games
    const sportFromGame = sports.find(
      (s) => s.name === achievement.sport_name
    );
    if (sportFromGame) {
      setForm((prev) => ({ ...prev, sport_ui: String(sportFromGame.id) }));
      loadGames(sportFromGame.id);
    }
  };

  const loadPendingAchievements = async () => {
    try {
      setLoadingPending(true);
      const res = await API.get("/admin/achievements/pending/all");
      setPendingAchievements(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load pending achievements", "error");
    } finally {
      setLoadingPending(false);
    }
  };

  const approvePending = async (id) => {
    try {
      const res = await API.put(`/admin/achievements/${id}/approve`);
      if (res.data?.success || res.success) {
        Swal.fire("Approved!", "Achievement has been approved.", "success");
        loadPendingAchievements();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to approve achievement", "error");
    }
  };

  const rejectPending = async (id) => {
    try {
      const res = await API.put(`/admin/achievements/${id}/reject`);
      if (res.data?.success || res.success) {
        Swal.fire("Rejected!", "Achievement has been rejected.", "success");
        loadPendingAchievements();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to reject achievement", "error");
    }
  };

  // --------- Effects ----------
  useEffect(() => {
    if (activeSubTab === "approve") {
      loadPendingAchievements();
    }
  }, [activeSubTab]);

  useEffect(() => {
    loadPlayers();
    loadSports();
    API.get("/me")
      .then((res) => {
        setCurrentUser(res);
      })
      .catch((err) => {
        console.error("Failed to load user info:", err);
      });
  }, []);

  useEffect(() => {
    loadAchievements();
  }, [loadAchievements]);

  useEffect(() => {
    loadPlayerClassifications(selectedPlayer);
  }, [selectedPlayer]);

  useEffect(() => {
    if (form.sport_ui) {
      loadGames(form.sport_ui);
    } else {
      setGames([]);
    }
  }, [form.sport_ui]);

  const selectedPlayerObj = useMemo(() => {
    return players.find((p) => String(p.id) === String(selectedPlayer));
  }, [players, selectedPlayer]);

  const selectedPlayerName = selectedPlayerObj?.name || "";

  const districts = useMemo(() => {
    const list = players.map((p) => p.district).filter(Boolean);
    const unique = Array.from(new Set(list));
    unique.sort((a, b) => a.localeCompare(b));
    return ["All", ...unique];
  }, [players]);

  const filteredPlayers = useMemo(() => {
    if (!selectedDistrict || selectedDistrict === "All") return players;
    return players.filter(
      (p) => String(p.district).toLowerCase() === selectedDistrict.toLowerCase()
    );
  }, [players, selectedDistrict]);

  const selectedGame = games.find((g) => String(g.id) === String(form.game_id));
  const selectedSportId = form.sport_ui || selectedGame?.sport_id || "";
  const selectedPlayerClassification = playerClassifications.find(
    (c) => String(c.sport_id) === String(selectedSportId)
  );

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-8">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
            🏅 Player Achievements
          </h1>
          <p className="text-sm text-gray-650">
            Medals • Awards • Competition History
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex bg-slate-100 p-1 rounded-xl max-w-fit mx-auto mb-8 border border-slate-200">
          <button
            onClick={() => setActiveSubTab("manage")}
            className={`px-4 py-2 rounded-lg font-bold text-xs transition-all ${
              activeSubTab === "manage" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Manage Achievements
          </button>
          <button
            onClick={() => setActiveSubTab("approve")}
            className={`px-4 py-2 rounded-lg font-bold text-xs transition-all ${
              activeSubTab === "approve" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Approve Pending ({pendingAchievements.length})
          </button>
        </div>

        {activeSubTab === "manage" ? (
          <>
            {/* Player Selector */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8 border border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                {/* Show District filter dropdown only for Admin / Secretary roles */}
                {(currentUser?.role === "admin" || currentUser?.role === "secretary") && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      📍 Filter by District
                    </label>
                    <select
                      value={selectedDistrict}
                      onChange={(e) => {
                        setSelectedDistrict(e.target.value);
                        setSelectedPlayer("");
                      }}
                      disabled={loading}
                      className="w-full p-3.5 border border-slate-300 rounded-xl bg-white text-slate-800 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    >
                      {districts.map((d) => (
                        <option key={d} value={d}>
                          {d === "All" ? "📋 All Districts" : d}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className={(currentUser?.role === "admin" || currentUser?.role === "secretary") ? "" : "col-span-2"}>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    👤 Select Player ({filteredPlayers.length} matches)
                  </label>
                  <SearchableSelect
                    value={selectedPlayer}
                    onChange={(e) => setSelectedPlayer(e.target.value)}
                    disabled={loading}
                    className="w-full border border-slate-300 rounded-xl text-slate-800 text-sm bg-white"
                    placeholder="🔍 Search or choose player..."
                  >
                    <option value="">-- Select Player --</option>
                    {filteredPlayers.map((player) => (
                      <option key={player.id} value={player.id}>
                        {player.name} {player.district ? `• ${player.district}` : ""}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              </div>

              {/* Show relation and dob detail box to prevent name collision */}
              {selectedPlayerObj && (
                <div className="mt-6 p-4 bg-emerald-50 border border-emerald-250 rounded-2xl flex flex-wrap gap-x-8 gap-y-3 text-sm text-emerald-800">
                  <div>
                    <span className="font-bold">Relation:</span> {selectedPlayerObj.relation || "N/A"}
                  </div>
                  <div>
                    <span className="font-bold">Date of Birth:</span> {selectedPlayerObj.dob || "N/A"}
                  </div>
                  <div>
                    <span className="font-bold">District:</span> {selectedPlayerObj.district || "N/A"}
                  </div>
                  <div>
                    <span className="font-bold">Player ID:</span> {selectedPlayerObj.registration_id || "N/A"}
                  </div>
                </div>
              )}

              {loading && (
                <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl text-center">
                  <div className="text-blue-800 font-semibold">
                    🔄 Loading players...
                  </div>
                </div>
              )}
            </div>

            {/* Form */}
            {selectedPlayer && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-3xl p-8 mb-8 shadow-2xl text-left">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  {editingId ? "✏️ Edit" : "➕ Add"} Achievement for{" "}
                  {selectedPlayerName}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Sport (UI only, optional) */}
                  <div>
                    <label className="block font-semibold mb-2">🏅 Sport</label>
                    <select
                    className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-purple-500"
                    value={form.sport_ui}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        sport_ui: e.target.value,
                        game_id: "",
                      })
                    }
                  >
                    <option value="">All sports / not specific</option>
                    {sports.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  </div>

                  {/* Game (stored as game_id) */}
                  <div>
                    <label className="block font-semibold mb-2">
                      🎮 Game / Event
                    </label>
                    <select
                      className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-purple-500"
                      value={form.game_id}
                      onChange={(e) =>
                        setForm({ ...form, game_id: e.target.value })
                      }
                      disabled={games.length === 0}
                    >
                      <option value="">
                        {games.length ? "Select game..." : "No games for sport"}
                      </option>
                      {games.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.game_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block font-semibold mb-2">♿ Classification</label>
                    <div className="w-full p-4 border rounded-xl bg-gray-50 min-h-[58px] flex items-center">
                      {selectedPlayerClassification?.classification ? (
                        <span className="font-semibold text-emerald-700">
                          {selectedPlayerClassification.classification}
                        </span>
                      ) : (
                        <span className="text-gray-500">
                          {selectedSportId ? "No classification assigned for selected sport" : "Select sport or game"}
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block font-semibold mb-2">🏅 Achievement Name *</label>
                    <input
                      type="text"
                      className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-purple-500 text-slate-800"
                      placeholder="E.g., Gold in Men's Shotput F57"
                      value={form.event_name}
                      onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-2">📍 Location</label>
                    <input
                      type="text"
                      className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-purple-500 text-slate-800"
                      placeholder="E.g., Chennai"
                      value={form.event_location}
                      onChange={(e) => setForm({ ...form, event_location: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-2">🏆 Event Level</label>
                    <select
                      className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-purple-500 text-slate-800"
                      value={form.event_level}
                      onChange={(e) => setForm({ ...form, event_level: e.target.value })}
                    >
                      <option value="District">District</option>
                      <option value="State">State</option>
                      <option value="National">National</option>
                      <option value="International">International</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-semibold mb-2">🎖️ Medal</label>
                    <select
                      className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-purple-500 text-slate-800"
                      value={form.medal}
                      onChange={(e) => setForm({ ...form, medal: e.target.value })}
                    >
                      <option value="None">None (Participation)</option>
                      <option value="Gold">Gold</option>
                      <option value="Silver">Silver</option>
                      <option value="Bronze">Bronze</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-semibold mb-2">📅 Event Date</label>
                    <input
                      type="date"
                      className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-purple-500 text-slate-800"
                      value={form.event_date}
                      onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                    />
                  </div>

                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block font-semibold mb-2">📝 Notes</label>
                    <textarea
                      className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-purple-500 text-slate-800"
                      rows={3}
                      placeholder="Enter notes..."
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex gap-4 mt-6 justify-end">
                  {editingId && (
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setForm({
                          id: null,
                          game_id: "",
                          sport_ui: "",
                          event_name: "",
                          event_location: "",
                          event_level: "District",
                          medal: "None",
                          event_date: "",
                          notes: "",
                        });
                      }}
                      className="px-6 py-3 border border-slate-300 text-slate-650 rounded-xl hover:bg-slate-50 font-bold"
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    onClick={saveAchievement}
                    disabled={saving}
                    className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-bold shadow-lg transition-all"
                  >
                    {saving ? "Saving..." : editingId ? "Update Achievement" : "Add Achievement"}
                  </button>
                </div>
              </div>
            )}

            {/* Achievements Table */}
            {selectedPlayer && achievements.length > 0 && (
              <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-lg text-left">
                <div className="px-6 py-4 bg-slate-50 border-b flex justify-between items-center">
                  <h3 className="font-extrabold text-lg text-slate-900">
                    Achievements List ({achievements.length})
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">
                        <th className="px-6 py-4">Sport & Game</th>
                        <th className="px-6 py-4">Classification</th>
                        <th className="px-6 py-4">Event</th>
                        <th className="px-6 py-4">Level</th>
                        <th className="px-6 py-4">Medal</th>
                        <th className="px-6 py-4">Date</th>
                        <th className="px-6 py-4">Notes</th>
                        <th className="px-6 py-4 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                      {achievements.map((achievement) => (
                        <tr key={achievement.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="font-bold text-slate-900">{achievement.sport_name || "—"}</div>
                            <div className="text-slate-400">{achievement.game_name || "—"}</div>
                          </td>
                          <td className="px-6 py-4 font-semibold text-emerald-700">{achievement.classification_name || "—"}</td>
                          <td className="px-6 py-4">
                            <div className="font-bold text-slate-900">{achievement.event_name}</div>
                            {achievement.event_location && <div className="text-slate-400 text-[10px]">{achievement.event_location}</div>}
                          </td>
                          <td className="px-6 py-4">
                            <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded text-[10px] font-bold">
                              {achievement.event_level}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider border ${
                              achievement.medal === "Gold" ? "bg-yellow-400/10 text-yellow-400 border-yellow-400/30" :
                              achievement.medal === "Silver" ? "bg-slate-100 text-slate-650 border-slate-200" :
                              achievement.medal === "Bronze" ? "bg-amber-600/10 text-amber-500 border-amber-500/20" :
                              "bg-slate-100 text-slate-500 border-slate-200"
                            }`}>
                              {achievement.medal === "None" ? "Participated" : achievement.medal}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-semibold">{achievement.event_date ? new Date(achievement.event_date).toLocaleDateString() : "TBD"}</td>
                          <td className="px-6 py-4 max-w-[200px] truncate">{achievement.notes || "—"}</td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={() => editAchievement(achievement)}
                                className="p-1.5 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg text-xs"
                                title="Edit"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => deleteAchievement(achievement.id)}
                                className="p-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg text-xs"
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty State */}
            {selectedPlayer && achievements.length === 0 && !loading && (
              <div className="text-center py-16 bg-slate-50 rounded-3xl border border-dashed border-slate-200 shadow-inner">
                <div className="text-5xl mb-4">🏆</div>
                <h3 className="text-xl font-extrabold text-slate-700 mb-2">No Achievements Added Yet</h3>
                <p className="text-xs text-slate-400 max-w-md mx-auto">
                  Add the first achievement for {selectedPlayerName} using the form above.
                </p>
              </div>
            )}
          </>
        ) : (
          
          /* APPROVALS SUB TAB */
          <div className="space-y-6 text-left">
            {loadingPending ? (
              <div className="text-center py-12 text-slate-500 font-semibold animate-pulse text-lg">Loading pending achievements...</div>
            ) : pendingAchievements.length === 0 ? (
              <div className="bg-white border rounded-2xl p-16 text-center text-slate-500 shadow-sm">
                <FaCheckCircle size={48} className="mx-auto text-emerald-250 mb-4 animate-pulse" />
                <p className="font-extrabold text-lg text-slate-800">All Caught Up!</p>
                <p className="text-xs text-slate-400 mt-1">No pending achievements require verification.</p>
              </div>
            ) : (
              <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">
                        <th className="px-6 py-4">Player Details</th>
                        <th className="px-6 py-4">Event Name</th>
                        <th className="px-6 py-4">Level</th>
                        <th className="px-6 py-4">Medal</th>
                        <th className="px-6 py-4">Location & Date</th>
                        <th className="px-6 py-4">Notes</th>
                        <th className="px-6 py-4">Proof</th>
                        <th className="px-6 py-4 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-xs divide-y divide-slate-100 text-slate-700">
                      {pendingAchievements.map((ach) => (
                        <tr key={ach.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="font-bold text-slate-900">{ach.player_name}</div>
                            <div className="text-[10px] text-slate-450">ID: {ach.player_registration_id} • Dist: {ach.player_district}</div>
                          </td>
                          <td className="px-6 py-4 font-bold text-slate-900">{ach.event_name}</td>
                          <td className="px-6 py-4">
                            <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded text-[10px] font-bold">
                              {ach.event_level}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider border ${
                              ach.medal === "Gold" ? "bg-yellow-400/10 text-yellow-400 border-yellow-400/30" :
                              ach.medal === "Silver" ? "bg-slate-150 text-slate-600 border-slate-200" :
                              ach.medal === "Bronze" ? "bg-amber-600/10 text-amber-500 border-amber-500/20" :
                              "bg-slate-100 text-slate-500 border-slate-200"
                            }`}>
                              {ach.medal === "None" ? "Participated" : ach.medal}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-semibold text-slate-800">{ach.event_location || "—"}</div>
                            {ach.event_date && <div className="text-slate-400 text-[10px]">{new Date(ach.event_date).toLocaleDateString()}</div>}
                          </td>
                          <td className="px-6 py-4 max-w-[200px] truncate">{ach.notes || "—"}</td>
                          <td className="px-6 py-4">
                            {ach.document_path ? (
                              <a
                                href={resolveAssetUrl(ach.document_path)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-emerald-600 hover:text-emerald-700 font-bold inline-flex items-center gap-1"
                              >
                                <FaFileAlt /> View file
                              </a>
                            ) : (
                              <span className="text-slate-400 italic">No file</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={() => rejectPending(ach.id)}
                                className="px-2.5 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-bold text-[10px]"
                              >
                                Reject
                              </button>
                              <button
                                onClick={() => approvePending(ach.id)}
                                className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-[10px]"
                              >
                                Approve
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default PlayerAchievements;
